import React, { useCallback, useMemo, useState } from 'react';
import { Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Layout from '../../core/components/layout/Layout';
import Pagination from '../../core/components/pagination/Pagination';
import Button from '../../core/components/button/Button';
import KeywordFiltersSection from '../../core/components/keyword-filters-section/KeywordFiltersSection';
import { StockBulkTable } from './StockBulkTable/StockBulkTable';
import { useBulkPaging } from './useBulkPaging';
import { useBulkSearch } from './useBulkSearch';
import { useBulkTable } from './useBulkTable';
import { useBulkInit } from './useBulkInit';
import { useBulkAction } from './useBulkAction';
import { getBulkOperationMessage, getStockPartRequestLinesUrl } from './utils';
import { PartRequestBulkItemDto } from './types';
import { EditLocationDialog } from './EditLocation/EditLocationDialog';
import { EditOwnerDialog } from './EditOwner/EditOwnerDialog';
import { getBulkRequestActionLabel } from './BulkRequestTypeEnum';
import { TruncateTooltip } from '../../common/components/truncate-tooltip/TruncateTooltip';
import DetailsHead from '../../core/components/details-head/DetailsHead';

import './StockBulk.scss';

const StockBulk = () => {
  const { bulkRequestType } = useBulkInit();
  const navigate = useNavigate();

  const [ownerEditRow, setOwnerEditRow] = useState<PartRequestBulkItemDto | null>(null);
  const [locationEditRow, setLocationEditRow] = useState<PartRequestBulkItemDto | null>(null);

  const pagingProps = useBulkPaging();
  const searchProps = useBulkSearch();
  const tableProps = useBulkTable(bulkRequestType);
  const { canProcessBulk, processBulk } = useBulkAction(bulkRequestType);
  const alertMessage = useMemo(() => getBulkOperationMessage(bulkRequestType), [bulkRequestType]);

  const handleBack = useCallback(() => {
    navigate(getStockPartRequestLinesUrl(), { replace: true });
  }, [navigate]);

  return <>
    <Layout subHeader={
      <DetailsHead className="StockBulk_header" goBack={handleBack}>
        <div className="StockBulk_header-inner">
          <TruncateTooltip value={alertMessage} className="StockBulk_title" />
          <Button
            designType="dark"
            label={getBulkRequestActionLabel(bulkRequestType)}
            disabled={!canProcessBulk}
            onClick={processBulk}
            icon={<FontAwesomeIcon icon={faCheckCircle} />}
            reversed
          />
        </div>

      </DetailsHead>
    }
    >
      <div className="actions-and-paging StockBulk_filter">
        {alertMessage && <Alert className="StockBulk_secondary-alert" message={alertMessage} />}
        <KeywordFiltersSection {...searchProps} placeholder="Search by customer" maxLength={250} isSearch isFullWidth />
        <Pagination {...pagingProps} showSizeChanger className="actions-and-paging__paging" narrow />
      </div>

      <StockBulkTable {...tableProps} bulkRequestType={bulkRequestType} onEditLocation={setLocationEditRow} onEditOwner={setOwnerEditRow} />
    </Layout>

    <EditLocationDialog row={locationEditRow} onClose={setLocationEditRow} />
    <EditOwnerDialog row={ownerEditRow} onClose={setOwnerEditRow} />
  </>;
};

export default StockBulk;
